/**
 * Tenant/Signup Service
 * Handles tenant registration and subdomain management
 */

import apiClient, { ApiError } from "./client";
import { ENDPOINTS, API_CONFIG } from "./config";

// Signup form data interface
export interface SignupFormData {
  // Company Info
  company_name: string;
  subdomain: string;

  // Contact Person
  first_name: string;
  last_name: string;
  email: string;
  email_verification_token: string; // proof of OTP verification (see sendSignupOtp/verifySignupOtp)
  phone: string;
  password: string; // User's password

  // Plan Selection
  plan_id: string; // "explorer", "officer", "commander", "titan"
  billing_cycle: "monthly" | "yearly";

  // Industry Vertical (for auto-setup)
  primary_vertical: string; // Required: "general", "hotel", "restaurant", etc.
  // Optional subtype: "pg", "hostel", "mess", "gov_contractor"... Resolves to
  // primary_vertical server-side; kept so iBOS can branch onboarding.
  vertical_subtype?: string | null;
  addon_verticals?: string[]; // Optional: additional industry modules

  // Structured address
  state?: string;
  city?: string;
  pincode?: string;
  address_line?: string;

  // GST (Optional - for B2B invoicing)
  gstin?: string;

  // Optional
  referral_code?: string;  // Partner affiliate code for commission tracking
  referral_source?: string;

  // Marketing attribution (first-touch, from ReferralTracker's axoix_attr cookie)
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer?: string;
  landing_page?: string;

  // Legal
  terms_accepted: boolean;
  privacy_accepted: boolean;

  // E-Signature data (for terms acceptance PDF)
  signature_png?: string;  // Base64 PNG of signature
  signature_type?: string; // "draw" | "type" | "upload"
  signature_timestamp?: string; // ISO timestamp
  signature_ip?: string;
  signature_user_agent?: string;
  sections_viewed?: { terms: boolean; privacy: boolean; cookies: boolean };
  legal_version?: string; // Which version of the legal text was signed (e.g. "2026-04-21")
}

// Subdomain check response
export interface SubdomainCheckResponse {
  available: boolean;
  subdomain: string;
  message?: string;
  is_pending?: boolean;
  masked_email?: string;
  resume_url?: string;
  company_name?: string;
  suggestions?: string[];
}

export interface RequestResumeRequest {
  subdomain?: string;
  email?: string;
  email_verification_token?: string;
}

export interface RequestResumeResponse {
  success: boolean;
  resumed?: boolean;
  sent?: boolean;
  resume_url?: string;
  masked_email?: string;
  message: string;
}

// Signup response
export interface SignupResponse {
  success: boolean;
  message: string;
  tenant_id?: string;
  subdomain?: string;
  dashboard_url?: string;
  requires_payment?: boolean;
  // For payment redirect
  payment_url?: string;
  order_id?: string;
}

// Send-OTP response
export interface SendSignupOtpResponse {
  success: boolean;
  message: string;
  cooldown_seconds: number;
}

// Verify-OTP response
export interface VerifySignupOtpResponse {
  success: boolean;
  message: string;
  email_verification_token: string | null;
}

/**
 * Send a 6-digit verification code to the given email.
 */
export const sendSignupOtp = async (email: string): Promise<SendSignupOtpResponse> => {
  try {
    const response = await apiClient.post<SendSignupOtpResponse>(
      ENDPOINTS.TENANTS.SEND_SIGNUP_OTP,
      { email }
    );
    return response.data;
  } catch (error) {
    const apiError = error as ApiError;
    throw new Error(apiError.message || "Failed to send verification code");
  }
};

/**
 * Verify a 6-digit code against the given email. Returns a short-lived token
 * that must be passed to signupTenant() as email_verification_token.
 */
export const verifySignupOtp = async (
  email: string,
  otp: string
): Promise<VerifySignupOtpResponse> => {
  try {
    const response = await apiClient.post<VerifySignupOtpResponse>(
      ENDPOINTS.TENANTS.VERIFY_SIGNUP_OTP,
      { email, otp }
    );
    return response.data;
  } catch (error) {
    const apiError = error as ApiError;
    throw new Error(apiError.message || "Invalid or expired code");
  }
};

/**
 * Check if subdomain is available
 */
export const checkSubdomain = async (subdomain: string, companyName?: string): Promise<SubdomainCheckResponse> => {
  try {
    // Sanitize subdomain (lowercase, alphanumeric and hyphens only)
    const sanitized = subdomain.toLowerCase().replace(/[^a-z0-9-]/g, "");

    const response = await apiClient.get<SubdomainCheckResponse>(
      `${ENDPOINTS.TENANTS.CHECK_SUBDOMAIN}/${sanitized}`
    );
    return response.data;
  } catch (error) {
    const apiError = error as ApiError;

    // Handle 409 Conflict (subdomain taken)
    if (apiError.status === 409) {
      return {
        available: false,
        subdomain: subdomain.toLowerCase(),
        message: "This subdomain is already taken",
        suggestions: generateSubdomainSuggestions(companyName || subdomain),
      };
    }

    throw new Error(apiError.message || "Failed to check subdomain availability");
  }
};

/**
 * Request a link to resume a pending registration
 */
export const requestResumeLink = async (
  data: RequestResumeRequest
): Promise<RequestResumeResponse> => {
  try {
    const response = await apiClient.post<RequestResumeResponse>(
      ENDPOINTS.TENANTS.REQUEST_RESUME,
      data
    );
    return response.data;
  } catch (error) {
    const apiError = error as ApiError;
    throw new Error(apiError.message || "Failed to request setup resume link");
  }
};

/**
 * Convert company name to a clean subdomain
 * "Rajat Enterprises" → "rajatenterprises"
 * "A.B.C. Technologies Pvt. Ltd." → "abctechnologies"
 */
export const companyToSubdomain = (companyName: string): string => {
  // Strip common suffixes that don't add value to a subdomain
  const noise = /\b(pvt|private|ltd|limited|llp|inc|corp|co|company|group|enterprises?|solutions?|services?|technologies|tech|systems?|industries)\b/gi;
  let clean = companyName.toLowerCase().replace(noise, "").trim();
  // If stripping removed everything, fall back to original
  if (clean.replace(/[^a-z0-9]/g, "").length < 3) {
    clean = companyName.toLowerCase();
  }
  return clean.replace(/[^a-z0-9]/g, "").slice(0, 30);
};

/**
 * Generate smart subdomain suggestions from the company name
 * Produces logical, readable alternatives — no random numbers or generic suffixes
 */
export const generateSubdomainSuggestions = (companyName: string): string[] => {
  const words = companyName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .split(/\s+/)
    .filter((w) => w.length > 0);

  const suggestions: string[] = [];
  const seen = new Set<string>();

  const add = (s: string) => {
    // Clean, validate length, skip dupes
    const c = s.replace(/[^a-z0-9-]/g, "").replace(/^-+|-+$/g, "").slice(0, 30);
    if (c.length >= 3 && !seen.has(c) && validateSubdomain(c).valid) {
      seen.add(c);
      suggestions.push(c);
    }
  };

  if (words.length >= 2) {
    // Hyphenated: "rajat-enterprises"
    add(words.join("-"));
    // First word + abbreviated second: "rajatent"
    add(words[0] + words[1].slice(0, 3));
    // First word + hyphen + abbreviated: "rajat-ent"
    add(words[0] + "-" + words[1].slice(0, 3));
    // Reversed: "enterprises-rajat"
    add([...words].reverse().join("-"));
    // Initials + last word: "re-solutions" (for 2+ words)
    const initials = words.map((w) => w[0]).join("");
    add(initials + "-" + words[words.length - 1]);
    // First word + last initial: "rajat-e"
    if (words[0].length >= 3) {
      add(words[0] + "-" + words[words.length - 1][0]);
    }
    // First + second hyphenated (first 2 words only for 3+ word names)
    if (words.length >= 3) {
      add(words[0] + "-" + words[1]);
    }
  } else if (words.length === 1) {
    const w = words[0];
    // Prefix variations
    add("the-" + w);
    add(w + "-hq");
    add(w + "-in");
    // Truncated if long
    if (w.length > 6) add(w.slice(0, 6));
    if (w.length > 8) add(w.slice(0, 4) + "-" + w.slice(4, 7));
  }

  return suggestions.slice(0, 5);
};

/**
 * Submit tenant signup
 */
export const signupTenant = async (data: SignupFormData): Promise<SignupResponse> => {
  try {
    // Validate required fields
    if (!data.company_name || !data.subdomain || !data.email) {
      throw new Error("Missing required fields");
    }

    if (!data.terms_accepted || !data.privacy_accepted) {
      throw new Error("You must accept the terms and privacy policy");
    }

    // Map frontend field names to backend expected names
    const requestBody = {
      company_name: data.company_name,
      subdomain: data.subdomain.toLowerCase().replace(/[^a-z0-9-]/g, ""),
      email: data.email,
      email_verification_token: data.email_verification_token,
      name: `${data.first_name} ${data.last_name}`.trim(),
      phone: data.phone || null,
      password: data.password,
      tier_id: data.plan_id || "explorer", // Map plan_id to tier_id
      billing_cycle: data.billing_cycle || "monthly",
      primary_vertical: data.primary_vertical || "general",
      // The flavour of that vertical when the visitor named one (?vertical=pg
      // -> "pg" while primary_vertical is "hotel"). This payload is built
      // field by field, so an unlisted key is silently dropped — that is why
      // it is here rather than only in the caller.
      vertical_subtype: data.vertical_subtype || null,
      addon_verticals: data.addon_verticals || [],
      terms_accepted: data.terms_accepted,
      privacy_accepted: data.privacy_accepted,
      referral_code: data.referral_code || null,
      // Marketing attribution
      utm_source: data.utm_source || null,
      utm_medium: data.utm_medium || null,
      utm_campaign: data.utm_campaign || null,
      referrer: data.referrer || null,
      landing_page: data.landing_page || null,
      // Address & GST
      state: data.state || null,
      city: data.city || null,
      pincode: data.pincode || null,
      address_line: data.address_line || null,
      gstin: data.gstin || null,
      // E-Signature data
      signature_png: data.signature_png || null,
      signature_type: data.signature_type || null,
      signature_timestamp: data.signature_timestamp || null,
      signature_ip: data.signature_ip || null,
      signature_user_agent: data.signature_user_agent || null,
      sections_viewed: data.sections_viewed || null,
      legal_version: data.legal_version || null,
    };

    const response = await apiClient.post<SignupResponse>(ENDPOINTS.TENANTS.SIGNUP, requestBody, {
      timeout: API_CONFIG.SIGNUP_TIMEOUT, // 2 min — provisioning is a 9-step pipeline
    });

    return response.data;
  } catch (error) {
    const apiError = error as ApiError;
    console.error("[Signup] API Error:", apiError);

    // Handle specific errors
    if (apiError.status === 409) {
      throw new Error("This subdomain or email is already registered");
    }
    if (apiError.status === 422) {
      // Try to extract validation details from the response
      const detail = (apiError as any).response?.data?.detail;
      if (Array.isArray(detail)) {
        // FastAPI validation error format
        const errors = detail.map((d: any) => `${d.loc?.slice(1).join('.')}: ${d.msg}`).join('; ');
        console.error("[Signup] Validation errors:", errors);
        throw new Error(`Validation failed: ${errors}`);
      } else if (typeof detail === 'string') {
        throw new Error(detail);
      }
      throw new Error("Invalid signup data. Please check all fields.");
    }

    throw new Error(apiError.message || "Failed to complete signup");
  }
};

/**
 * Validate subdomain format
 */
export const validateSubdomain = (subdomain: string): { valid: boolean; message?: string } => {
  const sanitized = subdomain.toLowerCase().trim();

  // Length check
  if (sanitized.length < 3) {
    return { valid: false, message: "Subdomain must be at least 3 characters" };
  }
  if (sanitized.length > 30) {
    return { valid: false, message: "Subdomain must be less than 30 characters" };
  }

  // Format check (alphanumeric and hyphens, no starting/ending with hyphen)
  const regex = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;
  if (!regex.test(sanitized)) {
    return {
      valid: false,
      message: "Subdomain must start and end with a letter or number, and contain only letters, numbers, and hyphens",
    };
  }

  // Reserved subdomains
  const reserved = [
    "www",
    "api",
    "admin",
    "app",
    "dashboard",
    "mail",
    "email",
    "support",
    "help",
    "blog",
    "shop",
    "store",
    "test",
    "demo",
    "staging",
    "dev",
    "hyperion",
    "indev",
    "mothership",
  ];
  if (reserved.includes(sanitized)) {
    return { valid: false, message: "This subdomain is reserved" };
  }

  return { valid: true };
};

const tenantService = {
  checkSubdomain,
  signupTenant,
  validateSubdomain,
  companyToSubdomain,
  generateSubdomainSuggestions,
  sendSignupOtp,
  verifySignupOtp,
};

export default tenantService;
